import { Link } from "react-router-dom";
import { ShieldCheck, XCircle } from "lucide-react";
import LegalLayout from "@/pages/legal/LegalLayout";
import { Button } from "@/components/ui/button";

export default function DriverConsent() {
  return (
    <LegalLayout title="Driver Data Consent" eyebrow="// Legal">
      <p>Before you submit an application to drive for <strong>Lucas Trans Inc.</strong>, please read this consent statement. You must accept it on the application form for us to review your submission.</p>

      <h2>What information we collect from applicants</h2>
      <ul>
        <li>Full name, city/state, primary and secondary phone numbers, email address.</li>
        <li>CDL class, endorsements, years of over-the-road experience, current employment status.</li>
        <li>Available start date, resume URL (optional), and any message you send with the application.</li>
      </ul>

      <h2>How we use it</h2>
      <ul>
        <li>To evaluate whether your experience matches our current openings.</li>
        <li>To contact you by phone, SMS or email about your application and next steps.</li>
        <li>To verify eligibility to work and CDL credentials, and to run pre-employment screening required by federal DOT regulations (49 CFR Part 391) if you advance in the process.</li>
      </ul>

      <h2>How we share it</h2>
      <p>Application data is accessed by Lucas Trans Inc. recruiting and safety staff, and by our transactional email provider (Resend). If you advance in the hiring process, additional verification vendors (MVR, PSP, drug screening) will be engaged, and you will be asked to sign the specific consents each of those steps requires under FCRA and DOT rules.</p>

      <h2>How long we keep it</h2>
      <p>Applications from candidates we do not hire are retained for up to <strong>3 years</strong> from the date of submission, then deleted or anonymized. Applications from hired drivers are retained for the duration of employment plus the retention windows required by DOT.</p>

      <h2>Your rights</h2>
      <p>You may request access, correction or deletion of your application data at any time by emailing <a href="mailto:office@lucastransinc.com">office@lucastransinc.com</a>. Deletion requests will be honored within 30 days unless a legal retention obligation applies.</p>

      <h2>Truthfulness</h2>
      <p>By accepting this consent statement you certify that the information you submit is accurate and complete to the best of your knowledge. Falsification of an application is grounds for immediate disqualification or termination.</p>

      <div className="mt-10 flex flex-wrap items-center gap-3 border-t border-[var(--lt-border)] pt-8">
        <Link to="/careers">
          <Button
            data-testid="consent-agree-btn"
            className="bg-[var(--lt-accent)] hover:bg-[var(--lt-accent-hover)] text-white rounded-xl h-12 px-6 font-head uppercase tracking-widest text-xs"
          >
            <ShieldCheck className="h-4 w-4 mr-2" /> Agree &amp; Continue to Application
          </Button>
        </Link>
        <Link to="/">
          <Button
            variant="outline"
            data-testid="consent-decline-btn"
            className="bg-transparent border-[var(--lt-border)] hover:bg-[var(--lt-panel-hover)] rounded-xl h-12 px-6 font-head uppercase tracking-widest text-xs"
          >
            <XCircle className="h-4 w-4 mr-2" /> Decline &amp; Return Home
          </Button>
        </Link>
      </div>

      <p className="text-xs text-slate-500 mt-4">
        You can also accept this consent inline on the application form itself — this page is provided so you can review the full statement any time.
      </p>
    </LegalLayout>
  );
}
